// Migração de domínio (multitenancy): formato novo é tenant.<base>/path
// (ex.: acme.webip.app/appliances), formato legado é <base>/<tenant>/path.
// Enquanto VITE_TENANT_BASE_DOMAIN não estiver definido, só o formato legado
// funciona — deploys sem DNS/certificado wildcard não mudam de comportamento.
// Aceita valor com esquema/porta (ex.: "https://dev.local:5173") — fica só o hostname.
function parseBaseDomain(raw: string | undefined | null): string | null {
    const cleaned = (raw ?? '')
        .trim()
        .toLowerCase()
        .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
        .split('/')[0]
        .split(':')[0];
    return cleaned || null;
}

export const TENANT_BASE_DOMAIN: string | null =
    parseBaseDomain(import.meta.env?.VITE_TENANT_BASE_DOMAIN as string | undefined);

export type TenantSource = 'subdomain' | 'path' | null;

const RESERVED_FIRST_SEGMENTS = new Set([
    "",
    "api",
    "socket.io",
    "login",
    "logout",
    "onboard",
    "usuario",
    "usuario_grupo",
    "appliances",
    "firewall",
    "nat",
    "dispositivos",
    "hotspot",
    "perfil",
    "relatorios",
    "portal",
]);

function normalizeTenant(value: string): string | null {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return null;
    if (!/^[a-z0-9][a-z0-9._-]*$/.test(normalized)) return null;
    return normalized;
}

export function getTenantFromPathname(pathname: string): string | null {
    const [segment = ""] = pathname.split("/").filter(Boolean);
    if (!segment || RESERVED_FIRST_SEGMENTS.has(segment.toLowerCase())) {
        return null;
    }

    return normalizeTenant(segment);
}

export function getTenantFromHostname(hostname: string): string | null {
    if (!TENANT_BASE_DOMAIN) return null;

    const host = hostname.trim().toLowerCase().split(":")[0];
    if (!host || host === TENANT_BASE_DOMAIN || host === `www.${TENANT_BASE_DOMAIN}`) {
        return null;
    }

    if (!host.endsWith(`.${TENANT_BASE_DOMAIN}`)) return null;

    return normalizeTenant(host.slice(0, -1 * (TENANT_BASE_DOMAIN.length + 1)));
}

/**
 * Resolve o tenant e a fonte de onde veio: subdomínio (formato novo) tem
 * precedência; senão cai no primeiro segmento do path (formato legado).
 */
export function resolveTenantFromWindow(): { tenant: string | null; source: TenantSource } {
    if (typeof window === "undefined") return { tenant: null, source: null };

    const tenantByHost = getTenantFromHostname(window.location.hostname);
    if (tenantByHost) return { tenant: tenantByHost, source: 'subdomain' };

    const tenantByPath = getTenantFromPathname(window.location.pathname);
    if (tenantByPath) return { tenant: tenantByPath, source: 'path' };

    return { tenant: null, source: null };
}

export function getTenantFromWindowPathname(): string | null {
    return resolveTenantFromWindow().tenant;
}

/**
 * Base pública que os instaladores de uma linha usam
 * (`curl -fsSL <base>/install.sh | sudo sh -s -- --token=...`).
 *
 * Sob subdomínio o tenant já está no host; no formato legado ele precisa
 * continuar no path, senão o script perde o tenant na chamada seguinte
 * (/install/bootstrap) — mesma regra de `installBaseUrl` no backend.
 */
export function getInstallBaseUrl(): string {
    if (typeof window === "undefined") return "";
    const { tenant, source } = resolveTenantFromWindow();
    return source === 'path' && tenant
        ? `${window.location.origin}/${tenant}`
        : window.location.origin;
}

export function getRouterBasename(): string | undefined {
    const { tenant, source } = resolveTenantFromWindow();
    // No formato novo as rotas ficam na raiz do subdomínio.
    if (source === 'subdomain') return undefined;
    if (!tenant) return undefined;
    return `/${tenant}`;
}

/**
 * Monta a URL no formato novo para o tenant, removendo o prefixo do tenant
 * do path legado (mesma técnica de performLoginRedirect).
 */
export function buildSubdomainUrl(tenant: string, fullPath: string): string | null {
    if (!TENANT_BASE_DOMAIN) return null;

    const tenantPrefix = tenant.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const routerPath = fullPath.replace(new RegExp(`^/${tenantPrefix}(?=/|$)`), '') || '/';
    const hash = typeof window !== 'undefined' ? (window.location.hash ?? '') : '';
    const path = `${routerPath}${hash}`;

    // Preserva protocolo e porta do acesso atual (teste local em http://dev.local:5173
    // redireciona para http://tenant.dev.local:5173; em produção fica https sem porta).
    const isHttp = typeof window !== 'undefined' && window.location?.protocol === 'http:';
    const port = typeof window !== 'undefined' ? (window.location.port ?? '') : '';
    const scheme = isHttp ? 'http' : 'https';
    const portSuffix = isHttp && port ? `:${port}` : '';
    return `${scheme}://${tenant}.${TENANT_BASE_DOMAIN}${portSuffix}${path}`;
}